import React, { BaseSyntheticEvent, Fragment, ReactElement } from 'react';
import PropTypes from 'prop-types';

import { SpacerVertical } from '@klarna/bubble-ui';
import { Card } from 'moet-core';

import { OnboardingFieldComponent } from '@/bpc/components/OnboardingFieldComponent/OnboardingFieldComponent';
import { OnboardingField } from '@/bpc/components/OnboardingFieldComponent/OnboardingField';
import { EncryptedString } from '@mjolnir/mf-fields-ts/src/types/Field.type';
import { ContentAnimationWrapper, LinkWithoutHref } from '@/components';

interface OnboardingFieldSetComponentProps {
  fields: OnboardingField[];
  setValueOfField: (
    field: OnboardingField,
    value: boolean | number | string | EncryptedString | string[],
  ) => void;
  getValueForField: (absolutePath: string) => boolean | number | string | EncryptedString;
  hasCardLayout?: boolean;
  onBlurField: (
    field: OnboardingField,
  ) => (value: string | boolean | number | EncryptedString | string[]) => void;
  onDelete?: () => void;
  t: TranslatorFunction;
  tKey: TranslatorFunctionKey;
  highlightMandatoryButEmptyFields: boolean;
}

interface FieldGroup {
  dependent: boolean;
  fields: OnboardingField[];
}

const groupFieldsByDependencyToApplyAnimations = (
  fields: OnboardingField[],
): Array<{ dependent: boolean; fields: OnboardingField[] }> => {
  const allFieldPaths = new Set(fields.map(f => f.absolutePath));
  const independentFields = fields
    .filter(f => !f.applicableDependsOn.some(d => allFieldPaths.has(d)))
    .filter(f => f.applicable());
  const dependentFields = fields.filter(f => f.applicableDependsOn.some(d => allFieldPaths.has(d)));
  const regroupedDependentFields = new Set();
  const groupedFields: FieldGroup[] = [{ dependent: false, fields: [] }];
  independentFields.forEach((independendField, i) => {
    groupedFields[groupedFields.length - 1].fields.push(independendField);
    const myDependentFields = dependentFields.filter(
      d =>
        !regroupedDependentFields.has(d.absolutePath) &&
        d.applicableDependsOn.indexOf(independendField.absolutePath) > -1,
    );
    if (myDependentFields.length > 0) {
      groupedFields.push({ dependent: true, fields: myDependentFields });
      myDependentFields.forEach(d => regroupedDependentFields.add(d.absolutePath));
      if (i !== independentFields.length - 1) {
        groupedFields.push({ dependent: false, fields: [] });
      }
    }
  });
  return groupedFields;
};

export const OnboardingFieldSetComponent = ({
  fields,
  setValueOfField,
  getValueForField,
  hasCardLayout,
  onBlurField,
  onDelete,
  t,
  tKey,
  highlightMandatoryButEmptyFields,
}: OnboardingFieldSetComponentProps): ReactElement => {
  const canDelete = onDelete != null;

  const onDeleteClick = (e: React.MouseEvent | BaseSyntheticEvent<any>): void => {
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    canDelete && onDelete();
  };

  const onChange = (f: OnboardingField) => (
    v: string | boolean | number | EncryptedString | string[],
  ) => setValueOfField(f, v);

  const renderOnboardingFieldComponents = (fields: OnboardingField[]): ReactElement[] =>
    fields.map(f => (
      <OnboardingFieldComponent
        key={f.absolutePath}
        onBlur={onBlurField(f)}
        field={f}
        onChange={onChange(f)}
        value={getValueForField(f.absolutePath)}
        t={t}
        tKey={tKey}
        highlightMandatoryButEmptyFields={highlightMandatoryButEmptyFields}
      />
    ));

  const renderAnimationWrappedOnboardingFieldComponents = (
    fields: OnboardingField[],
  ): ReactElement[] => {
    const isMoRFlow = fields.some(
      f =>
        f.payload?.contract_onboarding_type === 'MOR_ONBOARDING_ENTITY' ||
        f.payload?.contract_onboarding_type === 'MOR_ONBOARDING_GROUP',
    );
    return [
      <ContentAnimationWrapper
        key={fields.map(f => f.absolutePath).join('')}
        animation={fields.some(f => f.applicable()) ? 'visible' : 'hidden'}
      >
        {renderOnboardingFieldComponents(isMoRFlow ? fields.filter(f => f.applicable()) : fields)}
      </ContentAnimationWrapper>,
    ];
  };

  const Wrapper = hasCardLayout
    ? ({ children }): ReactElement => <Card hasFooterLink={canDelete}>{children}</Card>
    : Fragment;

  return (
    <Wrapper>
      {groupFieldsByDependencyToApplyAnimations(fields).flatMap(g =>
        g.dependent
          ? renderAnimationWrappedOnboardingFieldComponents(g.fields)
          : renderOnboardingFieldComponents(g.fields),
      )}

      <SpacerVertical spaceToken="space/150" />
      {canDelete && (
        <div style={{ textAlign: 'right' }}>
          <SpacerVertical spaceToken="space/50" />
          <LinkWithoutHref onPress={onDeleteClick}>Remove</LinkWithoutHref>
        </div>
      )}
    </Wrapper>
  );
};

OnboardingFieldSetComponent.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  getValueForField: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  tKey: PropTypes.func.isRequired,
  onBlurField: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};
